import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import {
  EFirmRoles,
  WhiteLabelSettingsFormControl,
  WhiteLabelSettingsFormKeys,
  WhiteLabelSettingsFormRawValue,
  WhiteLabelSettingsInputListItem,
} from '@atlas-workspace/shared/models';
import { parseInt } from 'lodash';
import { IConfig } from 'ngx-mask';

export const defaultWhiteLabelSettingsLogoPreviewPath = '/assets/client-logo-v3.svg';

export const whiteLabelHexColorInputPattern = /^#?([A-Fa-f0-9]{6})$/;

export const whiteLabelHexColorInputMaskPatternConfig: IConfig['patterns'] = {
  o: {
    pattern: /[A-Fa-f0-9]/,
  },
};

export const whiteLabelHexColorInputValidators = [
  Validators.required,
  Validators.pattern(whiteLabelHexColorInputPattern),
];

export function initWhiteLabelSettingsForm(): FormGroup<WhiteLabelSettingsFormControl> {
  return new FormGroup<WhiteLabelSettingsFormControl>({
    // General colors
    [WhiteLabelSettingsFormKeys.HeaderColor]: new FormControl(null, whiteLabelHexColorInputValidators),
    [WhiteLabelSettingsFormKeys.BackgroundColor]: new FormControl(null, whiteLabelHexColorInputValidators),
    [WhiteLabelSettingsFormKeys.BorderColor]: new FormControl(null, whiteLabelHexColorInputValidators),
    [WhiteLabelSettingsFormKeys.BorderHoverColor]: new FormControl(null, whiteLabelHexColorInputValidators),
    [WhiteLabelSettingsFormKeys.SidebarTabSelectedState]: new FormControl(null, whiteLabelHexColorInputValidators),
    [WhiteLabelSettingsFormKeys.SidebarTabHoveredState]: new FormControl(null, whiteLabelHexColorInputValidators),
    [WhiteLabelSettingsFormKeys.AccentColor]: new FormControl(null, whiteLabelHexColorInputValidators),
    [WhiteLabelSettingsFormKeys.OptionsHeaderColor]: new FormControl(null, whiteLabelHexColorInputValidators),
    [WhiteLabelSettingsFormKeys.CheckboxColor]: new FormControl(null, whiteLabelHexColorInputValidators),
    [WhiteLabelSettingsFormKeys.CheckboxHoverColor]: new FormControl(null, whiteLabelHexColorInputValidators),
    [WhiteLabelSettingsFormKeys.TabSelectedState]: new FormControl(null, whiteLabelHexColorInputValidators),
    [WhiteLabelSettingsFormKeys.TabHoveredState]: new FormControl(null, whiteLabelHexColorInputValidators),
    // Text
    [WhiteLabelSettingsFormKeys.PrimaryTextColor]: new FormControl(null, whiteLabelHexColorInputValidators),
    [WhiteLabelSettingsFormKeys.SecondaryTextColor]: new FormControl(null, whiteLabelHexColorInputValidators),
    [WhiteLabelSettingsFormKeys.PlaceholderTextColor]: new FormControl(null, whiteLabelHexColorInputValidators),
    [WhiteLabelSettingsFormKeys.DisabledTextColor]: new FormControl(null, whiteLabelHexColorInputValidators),
    // Primary button
    [WhiteLabelSettingsFormKeys.PrimaryButtonBackgroundRest]: new FormControl(null, whiteLabelHexColorInputValidators),
    [WhiteLabelSettingsFormKeys.PrimaryButtonBackgroundHover]: new FormControl(null, whiteLabelHexColorInputValidators),
    [WhiteLabelSettingsFormKeys.PrimaryButtonBackgroundDisabled]: new FormControl(
      null,
      whiteLabelHexColorInputValidators,
    ),
    [WhiteLabelSettingsFormKeys.PrimaryButtonBorderRest]: new FormControl(null, whiteLabelHexColorInputValidators),
    [WhiteLabelSettingsFormKeys.PrimaryButtonBorderHover]: new FormControl(null, whiteLabelHexColorInputValidators),
    [WhiteLabelSettingsFormKeys.PrimaryButtonBorderDisabled]: new FormControl(null, whiteLabelHexColorInputValidators),
    [WhiteLabelSettingsFormKeys.PrimaryButtonTextColorRest]: new FormControl(null, whiteLabelHexColorInputValidators),
    [WhiteLabelSettingsFormKeys.PrimaryButtonTextColorHover]: new FormControl(null, whiteLabelHexColorInputValidators),
    [WhiteLabelSettingsFormKeys.PrimaryButtonTextColorDisabled]: new FormControl(
      null,
      whiteLabelHexColorInputValidators,
    ),
    [WhiteLabelSettingsFormKeys.ButtonCornerRadius]: new FormControl(null, [Validators.required]),
    // Secondary button
    [WhiteLabelSettingsFormKeys.SecondaryButtonBackgroundRest]: new FormControl(
      null,
      whiteLabelHexColorInputValidators,
    ),
    [WhiteLabelSettingsFormKeys.SecondaryButtonBackgroundDisabled]: new FormControl(
      null,
      whiteLabelHexColorInputValidators,
    ),
    [WhiteLabelSettingsFormKeys.SecondaryButtonBackgroundHover]: new FormControl(
      null,
      whiteLabelHexColorInputValidators,
    ),
    [WhiteLabelSettingsFormKeys.SecondaryButtonTextColorRest]: new FormControl(null, whiteLabelHexColorInputValidators),
    [WhiteLabelSettingsFormKeys.SecondaryButtonTextColorHover]: new FormControl(
      null,
      whiteLabelHexColorInputValidators,
    ),
    [WhiteLabelSettingsFormKeys.SecondaryButtonTextColorDisabled]: new FormControl(
      null,
      whiteLabelHexColorInputValidators,
    ),
    [WhiteLabelSettingsFormKeys.SecondaryButtonBorderRest]: new FormControl(null, whiteLabelHexColorInputValidators),
    [WhiteLabelSettingsFormKeys.SecondaryButtonBorderHover]: new FormControl(null, whiteLabelHexColorInputValidators),
    [WhiteLabelSettingsFormKeys.SecondaryButtonBorderDisabled]: new FormControl(
      null,
      whiteLabelHexColorInputValidators,
    ),
    [WhiteLabelSettingsFormKeys.SecondaryButtonCornerRadius]: new FormControl(null, Validators.required),
    // Assets
    [WhiteLabelSettingsFormKeys.LogoUrl]: new FormControl(null),
    [WhiteLabelSettingsFormKeys.LogoFile]: new FormControl(null),
    [WhiteLabelSettingsFormKeys.LogoFileId]: new FormControl(null),
  });
}

export function fillEmptyFieldsByDefaultValuesOfPlainSettingsObj(
  plainObj: Partial<WhiteLabelSettingsFormRawValue>,
  defaultFormRawValue = whiteLabelSettingsDefaultInputsValues,
): void {
  Object.keys(plainObj).forEach((key) => {
    const typedKey = key as keyof typeof defaultFormRawValue;
    if (!plainObj[typedKey] && typedKey in defaultFormRawValue) {
      plainObj[typedKey] = defaultFormRawValue[typedKey];
    }
  });
}

export function setDefaultValueForEmptyControl(
  control: AbstractControl,
  key: WhiteLabelSettingsFormKeys,
  defaultFormRawValue = whiteLabelSettingsDefaultInputsValues,
  force = false,
): void {
  if (force || !control.value) {
    const plainObj = { [key]: null } as Partial<WhiteLabelSettingsFormRawValue>;
    fillEmptyFieldsByDefaultValuesOfPlainSettingsObj(plainObj, defaultFormRawValue);
    control.setValue(plainObj[key]);
  }
}

export const whiteLabelSettingsGeneralColorsInputsList: WhiteLabelSettingsInputListItem[] = [
  {
    formControlName: WhiteLabelSettingsFormKeys.HeaderColor,
    labelTranslateKey: 'WL_builder.Header_color',
  },
  {
    formControlName: WhiteLabelSettingsFormKeys.BackgroundColor,
    labelTranslateKey: 'WL_builder.Background_color',
  },
  {
    formControlName: WhiteLabelSettingsFormKeys.BorderColor,
    labelTranslateKey: 'WL_builder.Border_color',
  },
  {
    formControlName: WhiteLabelSettingsFormKeys.BorderHoverColor,
    labelTranslateKey: 'WL_builder.Border_hover_color',
  },
  {
    formControlName: WhiteLabelSettingsFormKeys.SidebarTabSelectedState,
    labelTranslateKey: 'WL_builder.Sidebar_tab_selected_state',
  },
  {
    formControlName: WhiteLabelSettingsFormKeys.SidebarTabHoveredState,
    labelTranslateKey: 'WL_builder.Sidebar_tab_hovered_state',
  },
  // TODO: hide temporarily [https://agilie.atlassian.net/browse/AT-12352?focusedCommentId=212133]
  // {
  //   formControlName: WhiteLabelSettingsFormKeys.CheckboxColor,
  //   labelTranslateKey: 'WL_builder.Checkbox_color',
  // },
  // {
  //   formControlName: WhiteLabelSettingsFormKeys.CheckboxHoverColor,
  //   labelTranslateKey: 'WL_builder.Checkbox_hover_color',
  // },
  {
    formControlName: WhiteLabelSettingsFormKeys.TabSelectedState,
    labelTranslateKey: 'WL_builder.Tab_selected_state',
  },
  // TODO: hide temporarily [https://agilie.atlassian.net/browse/AT-12352?focusedCommentId=212133]
  // {
  //   formControlName: WhiteLabelSettingsFormKeys.TabHoveredState,
  //   labelTranslateKey: 'WL_builder.Tab_hovered_state',
  // },
  {
    formControlName: WhiteLabelSettingsFormKeys.OptionsHeaderColor,
    labelTranslateKey: 'WL_builder.Options_dashboard_header',
  },
  {
    formControlName: WhiteLabelSettingsFormKeys.AccentColor,
    labelTranslateKey: 'WL_builder.Accent_color',
  },
];

export const whiteLabelSettingsTextInputsList: WhiteLabelSettingsInputListItem[] = [
  {
    formControlName: WhiteLabelSettingsFormKeys.PrimaryTextColor,
    labelTranslateKey: 'WL_builder.Primary',
  },
  {
    formControlName: WhiteLabelSettingsFormKeys.SecondaryTextColor,
    labelTranslateKey: 'WL_builder.Secondary',
  },
  {
    formControlName: WhiteLabelSettingsFormKeys.PlaceholderTextColor,
    labelTranslateKey: 'WL_builder.Placeholder',
  },
  {
    formControlName: WhiteLabelSettingsFormKeys.DisabledTextColor,
    labelTranslateKey: 'WL_builder.Disabled',
  },
];

export const whiteLabelSettingsPrimaryButtonInputsList: WhiteLabelSettingsInputListItem[] = [
  {
    formControlName: WhiteLabelSettingsFormKeys.PrimaryButtonBackgroundRest,
    labelTranslateKey: 'WL_builder.Primary_button_background_rest',
  },
  {
    formControlName: WhiteLabelSettingsFormKeys.PrimaryButtonBackgroundHover,
    labelTranslateKey: 'WL_builder.Primary_button_background_hover',
  },
  {
    formControlName: WhiteLabelSettingsFormKeys.PrimaryButtonBackgroundDisabled,
    labelTranslateKey: 'WL_builder.Primary_button_background_disabled',
  },
  {
    formControlName: WhiteLabelSettingsFormKeys.PrimaryButtonBorderRest,
    labelTranslateKey: 'WL_builder.Border_color',
  },
  {
    formControlName: WhiteLabelSettingsFormKeys.PrimaryButtonBorderHover,
    labelTranslateKey: 'WL_builder.Border_hover_color',
  },
  {
    formControlName: WhiteLabelSettingsFormKeys.PrimaryButtonBorderDisabled,
    labelTranslateKey: 'WL_builder.Border_disabled_color',
  },
  {
    formControlName: WhiteLabelSettingsFormKeys.PrimaryButtonTextColorRest,
    labelTranslateKey: 'WL_builder.Text_color',
  },
  {
    formControlName: WhiteLabelSettingsFormKeys.PrimaryButtonTextColorHover,
    labelTranslateKey: 'WL_builder.Text_hover',
  },
  {
    formControlName: WhiteLabelSettingsFormKeys.PrimaryButtonTextColorDisabled,
    labelTranslateKey: 'WL_builder.Text_disabled',
  },
  {
    formControlName: WhiteLabelSettingsFormKeys.ButtonCornerRadius,
    labelTranslateKey: 'WL_builder.Corner_radius',
    isNumeric: true,
    mask: '99',
  },
];

export const whiteLabelSettingsSecondaryButtonInputsList: WhiteLabelSettingsInputListItem[] = [
  {
    formControlName: WhiteLabelSettingsFormKeys.SecondaryButtonBackgroundRest,
    labelTranslateKey: 'WL_builder.Primary_button_background_rest',
  },
  {
    formControlName: WhiteLabelSettingsFormKeys.SecondaryButtonBackgroundHover,
    labelTranslateKey: 'WL_builder.Primary_button_background_hover',
  },
  {
    formControlName: WhiteLabelSettingsFormKeys.SecondaryButtonBackgroundDisabled,
    labelTranslateKey: 'WL_builder.Primary_button_background_disabled',
  },
  {
    formControlName: WhiteLabelSettingsFormKeys.SecondaryButtonBorderRest,
    labelTranslateKey: 'WL_builder.Border_color',
  },
  {
    formControlName: WhiteLabelSettingsFormKeys.SecondaryButtonBorderHover,
    labelTranslateKey: 'WL_builder.Border_hover_color',
  },
  {
    formControlName: WhiteLabelSettingsFormKeys.SecondaryButtonBorderDisabled,
    labelTranslateKey: 'WL_builder.Border_disabled_color',
  },
  {
    formControlName: WhiteLabelSettingsFormKeys.SecondaryButtonTextColorRest,
    labelTranslateKey: 'WL_builder.Text_color',
  },
  {
    formControlName: WhiteLabelSettingsFormKeys.SecondaryButtonTextColorHover,
    labelTranslateKey: 'WL_builder.Text_hover',
  },
  {
    formControlName: WhiteLabelSettingsFormKeys.SecondaryButtonTextColorDisabled,
    labelTranslateKey: 'WL_builder.Text_disabled',
  },
  {
    formControlName: WhiteLabelSettingsFormKeys.SecondaryButtonCornerRadius,
    labelTranslateKey: 'WL_builder.Corner_radius',
    isNumeric: true,
    mask: '99',
  },
];

export const whiteLabelSettingsDefaultInputsValues: Partial<WhiteLabelSettingsFormRawValue> = {
  // General colors
  [WhiteLabelSettingsFormKeys.HeaderColor]: '030D21',
  [WhiteLabelSettingsFormKeys.BackgroundColor]: 'F7F7F8',
  [WhiteLabelSettingsFormKeys.BorderColor]: 'EAECF0',
  [WhiteLabelSettingsFormKeys.BorderHoverColor]: 'C4C9D4',
  [WhiteLabelSettingsFormKeys.SidebarTabSelectedState]: 'EAECF0',
  [WhiteLabelSettingsFormKeys.SidebarTabHoveredState]: 'F1F2F4',
  [WhiteLabelSettingsFormKeys.AccentColor]: '0055FF',
  [WhiteLabelSettingsFormKeys.OptionsHeaderColor]: 'EAECF0',
  [WhiteLabelSettingsFormKeys.CheckboxColor]: '0055FF',
  [WhiteLabelSettingsFormKeys.CheckboxHoverColor]: '0044CC',
  [WhiteLabelSettingsFormKeys.TabSelectedState]: '0055FF',
  [WhiteLabelSettingsFormKeys.TabHoveredState]: '0044CC',
  // Text
  [WhiteLabelSettingsFormKeys.PrimaryTextColor]: '14181F',
  [WhiteLabelSettingsFormKeys.SecondaryTextColor]: '686E79',
  [WhiteLabelSettingsFormKeys.PlaceholderTextColor]: '8F96A3',
  [WhiteLabelSettingsFormKeys.DisabledTextColor]: 'C4C9D4',
  // Primary button
  [WhiteLabelSettingsFormKeys.PrimaryButtonBackgroundRest]: 'E5EEFF',
  [WhiteLabelSettingsFormKeys.PrimaryButtonBackgroundHover]: 'CCDDFF',
  [WhiteLabelSettingsFormKeys.PrimaryButtonBackgroundDisabled]: 'E5EEFF',
  [WhiteLabelSettingsFormKeys.PrimaryButtonBorderRest]: 'E5EEFF',
  [WhiteLabelSettingsFormKeys.PrimaryButtonBorderHover]: 'E5EEFF',
  [WhiteLabelSettingsFormKeys.PrimaryButtonBorderDisabled]: 'CCDDFF',
  [WhiteLabelSettingsFormKeys.PrimaryButtonTextColorRest]: '0055FF',
  [WhiteLabelSettingsFormKeys.PrimaryButtonTextColorHover]: '0044CC',
  [WhiteLabelSettingsFormKeys.PrimaryButtonTextColorDisabled]: '99bbff',
  [WhiteLabelSettingsFormKeys.ButtonCornerRadius]: 20,
  // Secondary button
  [WhiteLabelSettingsFormKeys.SecondaryButtonBackgroundRest]: 'FFFFFF',
  [WhiteLabelSettingsFormKeys.SecondaryButtonBackgroundDisabled]: 'FFFFFF',
  [WhiteLabelSettingsFormKeys.SecondaryButtonBackgroundHover]: 'FFFFFF',
  [WhiteLabelSettingsFormKeys.SecondaryButtonTextColorRest]: '14181F',
  [WhiteLabelSettingsFormKeys.SecondaryButtonTextColorHover]: '14181F',
  [WhiteLabelSettingsFormKeys.SecondaryButtonTextColorDisabled]: 'FFFFFF',
  [WhiteLabelSettingsFormKeys.SecondaryButtonBorderRest]: 'EAECF0',
  [WhiteLabelSettingsFormKeys.SecondaryButtonBorderHover]: 'C4C9D4',
  [WhiteLabelSettingsFormKeys.SecondaryButtonBorderDisabled]: 'FFFFFF',
  [WhiteLabelSettingsFormKeys.SecondaryButtonCornerRadius]: '32',
  // Assets
  [WhiteLabelSettingsFormKeys.LogoUrl]: defaultWhiteLabelSettingsLogoPreviewPath,
};

export const whiteLabelSettingsDefaultColorPickerPreviewParams = {
  backgroundColor: '#FFFFFF',
  borderColor: '#C4C9D4',
};

export const whiteLabelPreviewStyleVariables = {
  // General colors
  [WhiteLabelSettingsFormKeys.HeaderColor]: '--wl-headerColor',
  [WhiteLabelSettingsFormKeys.BackgroundColor]: '--wl-backgroundColor',
  [WhiteLabelSettingsFormKeys.BorderColor]: '--wl-borderColor',
  [WhiteLabelSettingsFormKeys.BorderHoverColor]: '--wl-borderHoverColor',
  [WhiteLabelSettingsFormKeys.SidebarTabSelectedState]: '--wl-sidebarTabSelectedState',
  [WhiteLabelSettingsFormKeys.SidebarTabHoveredState]: '--wl-sidebarTabHoveredState',
  [WhiteLabelSettingsFormKeys.AccentColor]: '--wl-accentColor',
  [WhiteLabelSettingsFormKeys.OptionsHeaderColor]: '--wl-optionsHeaderColor',
  [WhiteLabelSettingsFormKeys.CheckboxColor]: '--wl-checkboxColor',
  [WhiteLabelSettingsFormKeys.CheckboxHoverColor]: '--wl-checkboxHoverColor',
  [WhiteLabelSettingsFormKeys.TabSelectedState]: '--wl-tabSelectedState',
  [WhiteLabelSettingsFormKeys.TabHoveredState]: '--wl-tabHoveredState',
  // Text
  [WhiteLabelSettingsFormKeys.PrimaryTextColor]: '--wl-primaryTextColor',
  [WhiteLabelSettingsFormKeys.SecondaryTextColor]: '--wl-secondaryTextColor',
  [WhiteLabelSettingsFormKeys.PlaceholderTextColor]: '--wl-placeholderTextColor',
  [WhiteLabelSettingsFormKeys.DisabledTextColor]: '--wl-disabledTextColor',
  // Primary button
  [WhiteLabelSettingsFormKeys.PrimaryButtonBackgroundRest]: '--wl-primaryButtonBackgroundRest',
  [WhiteLabelSettingsFormKeys.PrimaryButtonBackgroundHover]: '--wl-primaryButtonBackgroundHover',
  [WhiteLabelSettingsFormKeys.PrimaryButtonBackgroundDisabled]: '--wl-primaryButtonBackgroundDisabled',
  [WhiteLabelSettingsFormKeys.PrimaryButtonBorderRest]: '--wl-primaryButtonBorderRest',
  [WhiteLabelSettingsFormKeys.PrimaryButtonBorderHover]: '--wl-primaryButtonBorderHover',
  [WhiteLabelSettingsFormKeys.PrimaryButtonBorderDisabled]: '--wl-primaryButtonBorderDisabled',
  [WhiteLabelSettingsFormKeys.PrimaryButtonTextColorRest]: '--wl-primaryButtonTextColorRest',
  [WhiteLabelSettingsFormKeys.PrimaryButtonTextColorHover]: '--wl-primaryButtonTextColorHover',
  [WhiteLabelSettingsFormKeys.PrimaryButtonTextColorDisabled]: '--wl-primaryButtonTextColorDisabled',
  [WhiteLabelSettingsFormKeys.ButtonCornerRadius]: '--wl-buttonCornerRadius',
  // Secondary button
  [WhiteLabelSettingsFormKeys.SecondaryButtonBackgroundRest]: '--wl-secondaryButtonBackgroundRest',
  [WhiteLabelSettingsFormKeys.SecondaryButtonBackgroundDisabled]: '--wl-secondaryButtonBackgroundDisabled',
  [WhiteLabelSettingsFormKeys.SecondaryButtonBackgroundHover]: '--wl-secondaryButtonBackgroundHover',
  [WhiteLabelSettingsFormKeys.SecondaryButtonTextColorRest]: '--wl-secondaryButtonTextColorRest',
  [WhiteLabelSettingsFormKeys.SecondaryButtonTextColorHover]: '--wl-secondaryButtonTextColorHover',
  [WhiteLabelSettingsFormKeys.SecondaryButtonTextColorDisabled]: '--wl-secondaryButtonTextColorDisabled',
  [WhiteLabelSettingsFormKeys.SecondaryButtonBorderRest]: '--wl-secondaryButtonBorderRest',
  [WhiteLabelSettingsFormKeys.SecondaryButtonBorderHover]: '--wl-secondaryButtonBorderHover',
  [WhiteLabelSettingsFormKeys.SecondaryButtonBorderDisabled]: '--wl-secondaryButtonBorderDisabled',
  [WhiteLabelSettingsFormKeys.SecondaryButtonCornerRadius]: '--wl-secondaryButtonCornerRadius',
};

export function formatWhiteLabelSettingsControlValue(key: WhiteLabelSettingsFormKeys, value: any): string {
  const isColor = [
    // General colors
    WhiteLabelSettingsFormKeys.HeaderColor,
    WhiteLabelSettingsFormKeys.BackgroundColor,
    WhiteLabelSettingsFormKeys.BorderColor,
    WhiteLabelSettingsFormKeys.BorderHoverColor,
    WhiteLabelSettingsFormKeys.SidebarTabSelectedState,
    WhiteLabelSettingsFormKeys.SidebarTabHoveredState,
    WhiteLabelSettingsFormKeys.AccentColor,
    WhiteLabelSettingsFormKeys.OptionsHeaderColor,
    WhiteLabelSettingsFormKeys.CheckboxColor,
    WhiteLabelSettingsFormKeys.CheckboxHoverColor,
    WhiteLabelSettingsFormKeys.TabSelectedState,
    WhiteLabelSettingsFormKeys.TabHoveredState,
    // Text
    WhiteLabelSettingsFormKeys.PrimaryTextColor,
    WhiteLabelSettingsFormKeys.SecondaryTextColor,
    WhiteLabelSettingsFormKeys.PlaceholderTextColor,
    WhiteLabelSettingsFormKeys.DisabledTextColor,
    // Primary button
    WhiteLabelSettingsFormKeys.PrimaryButtonBackgroundRest,
    WhiteLabelSettingsFormKeys.PrimaryButtonBackgroundHover,
    WhiteLabelSettingsFormKeys.PrimaryButtonBackgroundDisabled,
    WhiteLabelSettingsFormKeys.PrimaryButtonBorderRest,
    WhiteLabelSettingsFormKeys.PrimaryButtonBorderHover,
    WhiteLabelSettingsFormKeys.PrimaryButtonBorderDisabled,
    WhiteLabelSettingsFormKeys.PrimaryButtonTextColorRest,
    WhiteLabelSettingsFormKeys.PrimaryButtonTextColorHover,
    WhiteLabelSettingsFormKeys.PrimaryButtonTextColorDisabled,
    // Secondary button
    WhiteLabelSettingsFormKeys.SecondaryButtonBackgroundRest,
    WhiteLabelSettingsFormKeys.SecondaryButtonBackgroundDisabled,
    WhiteLabelSettingsFormKeys.SecondaryButtonBackgroundHover,
    WhiteLabelSettingsFormKeys.SecondaryButtonTextColorRest,
    WhiteLabelSettingsFormKeys.SecondaryButtonTextColorHover,
    WhiteLabelSettingsFormKeys.SecondaryButtonTextColorDisabled,
    WhiteLabelSettingsFormKeys.SecondaryButtonBorderRest,
    WhiteLabelSettingsFormKeys.SecondaryButtonBorderHover,
    WhiteLabelSettingsFormKeys.SecondaryButtonBorderDisabled,
  ].includes(key);
  if (isColor) {
    return `#${value}`;
  }
  const isBorderRadius = [
    WhiteLabelSettingsFormKeys.ButtonCornerRadius,
    WhiteLabelSettingsFormKeys.SecondaryButtonCornerRadius,
  ].includes(key);
  if (isBorderRadius) {
    return `${value}px`;
  }
  return value;
}

export function isLightHexColor(hex: string): boolean {
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  const brightnessDegree = 180;

  return luminance > brightnessDegree;
}

export function hasAccessToWhiteLabelBuilder(firmRole: string): boolean {
  return firmRole === EFirmRoles.PrimaryOwner || firmRole === EFirmRoles.Admin;
}

export function toFormDataToUpdate(formRawValue: WhiteLabelSettingsFormRawValue): FormData {
  const fd = new FormData();
  const setPrimitiveFormDataValue = (key: WhiteLabelSettingsFormKeys): void =>
    fd.append(`white_label[${key}]`, String(formRawValue[key]));
  // General colors
  setPrimitiveFormDataValue(WhiteLabelSettingsFormKeys.HeaderColor);
  setPrimitiveFormDataValue(WhiteLabelSettingsFormKeys.BackgroundColor);
  setPrimitiveFormDataValue(WhiteLabelSettingsFormKeys.BorderColor);
  setPrimitiveFormDataValue(WhiteLabelSettingsFormKeys.BorderHoverColor);
  setPrimitiveFormDataValue(WhiteLabelSettingsFormKeys.SidebarTabSelectedState);
  setPrimitiveFormDataValue(WhiteLabelSettingsFormKeys.SidebarTabHoveredState);
  setPrimitiveFormDataValue(WhiteLabelSettingsFormKeys.AccentColor);
  setPrimitiveFormDataValue(WhiteLabelSettingsFormKeys.OptionsHeaderColor);
  setPrimitiveFormDataValue(WhiteLabelSettingsFormKeys.CheckboxColor);
  setPrimitiveFormDataValue(WhiteLabelSettingsFormKeys.CheckboxHoverColor);
  setPrimitiveFormDataValue(WhiteLabelSettingsFormKeys.TabSelectedState);
  setPrimitiveFormDataValue(WhiteLabelSettingsFormKeys.TabHoveredState);
  // Text
  setPrimitiveFormDataValue(WhiteLabelSettingsFormKeys.PrimaryTextColor);
  setPrimitiveFormDataValue(WhiteLabelSettingsFormKeys.SecondaryTextColor);
  setPrimitiveFormDataValue(WhiteLabelSettingsFormKeys.PlaceholderTextColor);
  setPrimitiveFormDataValue(WhiteLabelSettingsFormKeys.DisabledTextColor);
  // Primary button
  setPrimitiveFormDataValue(WhiteLabelSettingsFormKeys.PrimaryButtonBackgroundRest);
  setPrimitiveFormDataValue(WhiteLabelSettingsFormKeys.PrimaryButtonBackgroundHover);
  setPrimitiveFormDataValue(WhiteLabelSettingsFormKeys.PrimaryButtonBackgroundDisabled);
  setPrimitiveFormDataValue(WhiteLabelSettingsFormKeys.PrimaryButtonBorderRest);
  setPrimitiveFormDataValue(WhiteLabelSettingsFormKeys.PrimaryButtonBorderHover);
  setPrimitiveFormDataValue(WhiteLabelSettingsFormKeys.PrimaryButtonBorderDisabled);
  setPrimitiveFormDataValue(WhiteLabelSettingsFormKeys.PrimaryButtonTextColorRest);
  setPrimitiveFormDataValue(WhiteLabelSettingsFormKeys.PrimaryButtonTextColorHover);
  setPrimitiveFormDataValue(WhiteLabelSettingsFormKeys.PrimaryButtonTextColorDisabled);
  setPrimitiveFormDataValue(WhiteLabelSettingsFormKeys.ButtonCornerRadius);
  // Secondary button
  setPrimitiveFormDataValue(WhiteLabelSettingsFormKeys.SecondaryButtonBackgroundRest);
  setPrimitiveFormDataValue(WhiteLabelSettingsFormKeys.SecondaryButtonBackgroundDisabled);
  setPrimitiveFormDataValue(WhiteLabelSettingsFormKeys.SecondaryButtonBackgroundHover);
  setPrimitiveFormDataValue(WhiteLabelSettingsFormKeys.SecondaryButtonTextColorRest);
  setPrimitiveFormDataValue(WhiteLabelSettingsFormKeys.SecondaryButtonTextColorHover);
  setPrimitiveFormDataValue(WhiteLabelSettingsFormKeys.SecondaryButtonTextColorDisabled);
  setPrimitiveFormDataValue(WhiteLabelSettingsFormKeys.SecondaryButtonBorderRest);
  setPrimitiveFormDataValue(WhiteLabelSettingsFormKeys.SecondaryButtonBorderHover);
  setPrimitiveFormDataValue(WhiteLabelSettingsFormKeys.SecondaryButtonBorderDisabled);
  setPrimitiveFormDataValue(WhiteLabelSettingsFormKeys.SecondaryButtonCornerRadius);
  // Assets
  const logoFileId = formRawValue[WhiteLabelSettingsFormKeys.LogoFileId];
  const logoFile = formRawValue[WhiteLabelSettingsFormKeys.LogoFile];
  const logoUrl = formRawValue[WhiteLabelSettingsFormKeys.LogoUrl];
  if (logoFileId && !logoFile && !logoUrl) {
    // No new logo, remove old logo - delete file
    fd.append('white_label[logo_attributes][id]', String(logoFileId));
    fd.append('white_label[logo_attributes][_destroy]', String(true));
  } else if (logoFile && !logoFileId) {
    // Has new logo, no old logo - upload file
    fd.append('white_label[logo_attributes][filename]', logoFile);
  } else if (logoFile && logoFileId) {
    // Has new logo, has old logo - replace file
    fd.append('white_label[logo_attributes][id]', String(logoFileId));
    fd.append('white_label[logo_attributes][filename]', logoFile);
  }
  return fd;
}
